<template>
  <div class="coze-core">
    <div class="message-box">
      <div class="message-box__info">
        <div class="bot-info">
          <img class="bot-info__avatar" :src="botAvatar" />
          <span class="bot-info__name">{{
            props.config?.practiceContent?.customerName || '张经理'
          }}</span>
        </div>
      </div>
      <!-- 消息对话 -->
      <div class="message-list__box">
        <ul class="message-list">
          <li v-for="message in messageList" :key="message.eventName" class="message-list__item">
            <ChatCard :bot-info="props.botInfo" :message="message"></ChatCard>
          </li>
          <div class="message-list__cover"></div>
        </ul>
      </div>

      <div class="message-status">
        <template v-if="endedReason">
          <div class="message-status__end-reason">
            {{ endedReason }}
          </div>
          <div class="message-status__duration message-status__duration--ended">
            {{ callDurationInfo.durationStr }}
          </div>
        </template>
        <template v-else-if="clientStatus">
          <!-- 声纹展示暂时注释
          <div class="message-status__voice-bar">
            <VolumeLine :volume="currVolume"></VolumeLine>
          </div>
          -->
          <div class="message-status__duration">
            {{ callDurationInfo.durationStr }}
          </div>
        </template>
        <template v-else>
          <div class="message-status__calling">连接中...</div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import h5Api from '@/utils/h5/h5-others'
import ARTCAICallclient, { AICallAgentConfig, AICallTemplateConfig } from 'aliyun-auikit-aicall'
import {
  onMounted,
  ref,
  watch,
  defineProps,
  computed,
  onUnmounted,
  defineExpose,
  defineEmits,
  defineOptions
} from 'vue'
import ChatCard from './chat-card/index.vue'
// import VolumeLine from '../volume-line.vue'
import { parseAliPracticeEndContent } from '../../ali-end-protocol'

defineOptions({
  name: 'AliCore'
})

// emit
const emit = defineEmits(['on-start', 'on-end'])

// props
const props = defineProps({
  config: {
    type: Object,
    required: true,
    default: () => ({})
  }
})

const botAvatar = require('@/assets/ai-avatar/ai-practice-partner-man-bg.png')

const timeInfo = ref({
  startTime: 0,
  endTime: 0
})
// 实时音量大小调节，声纹展示暂时注释
// const currVolume = ref(0)
const durationInterval = ref(null)
const endedReason = ref('')
// 阿里智能体
let client = null
const clientStatus = ref(false)

// 智能体实体Id
const instanceId = ref('')
// 对话历史
const messageList = ref([])

const callDurationInfo = computed(() => {
  if (endedReason.value) {
    return {
      duration: 0,
      durationStr: '对方已挂断'
    }
  }

  const duration = timeInfo.value.endTime - timeInfo.value.startTime
  const seconds = Math.floor(duration / 1000)

  // 计算分钟和秒
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return {
    duration: seconds,
    durationStr: `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
})

const endCallByReason = (endReason = '') => {
  if (endedReason.value) {
    return
  }

  endedReason.value = endReason
  clientStatus.value = false
  stopClient({ preserveEndReason: true })
  emit('on-end', endReason)
}

const upsertAssistantMessage = (subtitle = {}) => {
  const eventName = `agentSubtitleNotify-${subtitle?.sentenceId}`
  const oldMessage = messageList.value.find((item) => item.eventName === eventName)
  const baseContent = oldMessage?.data?.rawContent || oldMessage?.data?.content || ''
  const mergedContent = `${baseContent}${subtitle.text || ''}`
  const { displayContent, endReason, hasEndMarker } = parseAliPracticeEndContent(mergedContent)

  if (oldMessage) {
    oldMessage.data.rawContent = mergedContent
    oldMessage.data.content = displayContent
    oldMessage.timestamp = Date.now()
  } else {
    messageList.value.push({
      eventName,
      data: {
        role: 'assistant',
        content: displayContent,
        rawContent: mergedContent
      },
      timestamp: Date.now()
    })
  }

  if (hasEndMarker) {
    endCallByReason(endReason)
  }
}

const initBefore = async () => {
  // 1.检查设备权限
  if (window.plus) {
    h5Api.h5ChoosePermission({ type: 4, onSuccess: init })
  } else {
    init()
  }
  // const result = await RealtimeUtils.checkDevicePermission()
  // if (!result.audio) {
  //   // throw new Error('需要麦克风访问权限')
  //   if (window.plus) {
  //     h5Api.h5ChoosePermission({ type: 4, onSuccess: init })
  //   } else {
  //     init()
  //   }
  // } else {
  //   init()
  // }
}

const init = async () => {
  client = new ARTCAICallclient()
  console.log('🚀 ~ init ~ client:', client)
  const agentConfig = new AICallAgentConfig()
  agentConfig.fromJSON(props.config.agentConfig)
  const templateConfig = new AICallTemplateConfig()
  templateConfig.asrLanguageId = 'zh_en'
  client.callWithConfig({
    ...props.config,
    agentConfig,
    templateConfig,
    agentType: 0
  })

  // instanceId.value = client
  // 载入智能体回调
  initBotCallback()
}

const initBotCallback = () => {
  // 如果启动通话失败，会触发下面回调
  client.on('errorOccurred', (code) => {
    // 通过过程发生了错误，进行挂断
    client.handup()
    console.error(code)
  })

  // 如果智能体启动通话成功，会触发下面回调
  client.on('agentStarted', () => {
    // 智能体实例已启动
    clientStatus.value = true
    onCallDuration()
    emit('on-start')

    // 智能体启动成功后 拿到阿里智能体实体id
    instanceId.value = client.agentInfo.instanceId
  })

  // 智能体音量发生变化，声纹展示暂时注释
  // client.on('speakingVolumeChanged', (_, volume) => {
  //   currVolume.value = Math.max(0, Math.min(Number(volume) || 0, 100))
  // })

  // 智能体状态改变
  client.on('agentStateChange', (newState) => {
    // console.log(newState)
  })

  // 监听智能体字幕
  client.on('agentSubtitleNotify', (subtitle) => {
    console.log(subtitle)
    if (!subtitle?.text) return

    upsertAssistantMessage(subtitle)
  })

  // 监听用户字幕
  client.on('userSubtitleNotify', (subtitle) => {
    if (!subtitle?.text) return

    const eventName = `userSubtitleNotify-${subtitle?.sentenceId}`
    const oldMessage = messageList.value.find((item) => item.eventName === eventName)

    if (oldMessage) {
      oldMessage.data.content = subtitle.text
      oldMessage.timestamp = Date.now()
      return
    }

    messageList.value.push({
      eventName,
      data: {
        role: 'sale',
        content: subtitle.text
      },
      timestamp: Date.now()
    })
  })
}

const stopClient = (options = {}) => {
  const { preserveEndReason = false } = options

  if (client) {
    client.handup()
    // 挂断后释放资源
    client.destroy()
    client = null
  }
  clearInterval(durationInterval.value)
  durationInterval.value = null
  // currVolume.value = 0

  if (!preserveEndReason) {
    endedReason.value = ''
  }
}

// 拨号 计时器
const onCallDuration = () => {
  timeInfo.value.startTime = Date.now()
  timeInfo.value.endTime = timeInfo.value.startTime
  durationInterval.value = setInterval(() => {
    timeInfo.value.endTime = Date.now()
  }, 1000)
}

// // 判断点是否激活（从中间向两边）
// const isDotActive = (index) => {
//   const center = 15 // 30个点的中心位置
//   const distance = Math.abs(index - center)
//   // 根据音量计算激活半径
//   const activeRadius = index * 0.5
//   return distance <= activeRadius
// }

defineExpose({
  stopClient,
  instanceId,
  messageList,
  timeInfo,
  endedReason
})

// lice-cycle
onMounted(async () => {
  watch(
    () => props.config,
    async (val = {}) => {
      if (val.userJoinToken) {
        initBefore()
      }
    }
  )
})
onUnmounted(() => {
  stopClient()
})
</script>

<style lang="less" scoped>
.coze-core {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  .client-operate {
    flex-shrink: 0;
    padding: 10px;
    border-bottom: 1px solid #ccc;
    background-color: #f5f5f5;
    display: flex;
    gap: 10px;
  }

  .message-box {
    flex-grow: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    &__info {
      margin-top: 16px;
      width: 100%;
      position: relative;
      // flex-grow: 1;
      flex-shrink: 0;
      display: flex;
      flex-direction: column-reverse;

      .bot-info {
        // margin-top: 80px;
        margin: 0 auto;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 4px 12px 4px 4px;
        background: #ebecee;
        border-radius: 64px;

        &__avatar {
          width: 24px;
          height: 24px;
        }
        &__name {
          line-height: 24px;
          margin-left: 8px;
          color: #323233;
          font-size: 14px;

          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        // &__desc {
        //   margin-top: 16px;
        //   font-size: 14px;
        //   color: #646566;
        //   overflow: hidden;
        //   text-overflow: ellipsis;
        //   white-space: nowrap;
        //   width: 80%;
        //   padding: 0 12px;
        //   box-sizing: border-box;
        // }
      }
    }

    .message-list__box {
      width: 100%;
      box-sizing: border-box;
      flex-grow: 1;
      min-height: 0;
      flex-shrink: 1;
      // max-height: 40vh;
      // flex-grow: 1;
      // min-height: 0;
      position: relative;
      margin: 24px 0;
      // height: 308px;
      // max-height: 40vh;

      // &::before {
      //   content: '';
      //   z-index: 1;
      //   left: 0;
      //   position: absolute;
      //   width: 100%;
      //   top: -1px;
      //   height: 24px;
      //   background: linear-gradient(
      //     180deg,
      //     rgba(242, 243, 245, 0.5) 0%,
      //     rgba(242, 243, 245, 0) 100%
      //   );
      //   backdrop-filter: blur(0px);
      // }

      .message-list {
        position: relative;
        width: 100%;
        height: 100%;
        padding: 0 24px;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        // overflow: auto;
        overflow: hidden;

        &__item {
          margin-top: 20px;
        }
      }
    }
  }

  .message-status {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    // &__voice-bar {
    // }
    &__end-reason {
      width: fit-content;
      max-width: calc(100% - 24px);
      box-sizing: border-box;
      margin: 0 12px;
      padding: 4px 12px;
      border-radius: 64px;
      background: #e5e6eb;
      color: #646566;
      font-size: 12px;
      line-height: 16px;
      text-align: left;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: normal;
      word-break: break-word;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    &__duration {
      color: #969799;
      font-size: 13px;
      margin-top: 24px;

      &--ended {
        color: #f54e4e;
        margin-top: 20px;
      }
    }

    &__calling {
      color: #323232;
      font-size: 16px;
      margin-top: 24px;
    }
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
